import { diagnosticEventLog } from "@/shared/services/diagnostic/DiagnosticEventLog";
import type { DiagnosticSource } from "@/shared/services/diagnostic/types";

export interface RetryManagerOptions {
  connectionName: string;
  reconnectDelay: number;
  fallbackThreshold: number;
  sseStabilityWindowMs: number;
  logger: (...args: unknown[]) => void;
  /** Called when a retry attempt fires — main class calls connect() */
  onRetryAttempt: () => void;
  /** Called when retry threshold reached — main class enters polling mode */
  onThresholdReached: () => void;
  /** Called when no subscribers remain during backoff — main class sets status inactive */
  onNoSubscribers: () => void;
  /** Checks if any subscribers exist (guards reconnect) */
  hasSubscribers: () => boolean;
  /** Whether fallback mode is available (guards threshold check) */
  fallbackEnabled: boolean;
}

/**
 * Manages retry logic with exponential backoff and SSE stability tracking.
 * Delegates actual connection/polling to the main class via callbacks.
 */
export class RetryManager {
  retryCount = 0;
  isRetrying = false;
  activityTimeoutCount = 0;

  private retryTimerRef: ReturnType<typeof setTimeout> | undefined;
  private sseStabilityTimer: ReturnType<typeof setTimeout> | undefined;

  private readonly connectionName: string;
  private readonly reconnectDelay: number;
  private readonly fallbackThreshold: number;
  private readonly sseStabilityWindowMs: number;
  private readonly logger: (...args: unknown[]) => void;
  private readonly onRetryAttempt: () => void;
  private readonly onThresholdReached: () => void;
  private readonly onNoSubscribers: () => void;
  private readonly hasSubscribers: () => boolean;
  private readonly fallbackEnabled: boolean;
  private readonly diagnosticSource: DiagnosticSource;

  constructor(options: RetryManagerOptions) {
    this.connectionName = options.connectionName;
    this.reconnectDelay = options.reconnectDelay;
    this.fallbackThreshold = options.fallbackThreshold;
    this.sseStabilityWindowMs = options.sseStabilityWindowMs;
    this.logger = options.logger;
    this.onRetryAttempt = options.onRetryAttempt;
    this.onThresholdReached = options.onThresholdReached;
    this.onNoSubscribers = options.onNoSubscribers;
    this.hasSubscribers = options.hasSubscribers;
    this.fallbackEnabled = options.fallbackEnabled;
    this.diagnosticSource = `${options.connectionName.toLowerCase()}:sse`;
  }

  /**
   * Schedule a reconnect attempt with exponential backoff.
   * If retry threshold is reached and polling configs exist, switches to polling.
   */
  scheduleReconnect(): void {
    if (!this.hasSubscribers()) {
      this.logger(
        `[${this.connectionName}] No subscribers remaining, skipping reconnection`
      );
      this.onNoSubscribers();
      return;
    }

    this.retryCount++;

    // Check if we should switch to polling mode
    if (
      this.retryCount >= this.fallbackThreshold &&
      this.fallbackEnabled
    ) {
      this.logger(
        `[${this.connectionName}] retryCount (${this.retryCount}) >= threshold (${this.fallbackThreshold}), switching to polling mode`
      );
      this.onThresholdReached();
      return;
    }

    const delayInMs = this.retryCount * this.reconnectDelay;

    diagnosticEventLog.record(this.diagnosticSource, "info", "retry-scheduled", {
      retryCount: this.retryCount,
      delayMs: delayInMs,
      fallbackThreshold: this.fallbackThreshold,
    });

    this.logger(
      `[${this.connectionName}] Reconnecting in ${delayInMs / 1000} seconds... (retryCount=${this.retryCount})`
    );

    this.isRetrying = true;

    this.retryTimerRef = setTimeout(() => {
      this.isRetrying = false;

      if (this.hasSubscribers()) {
        this.onRetryAttempt();
      } else {
        this.logger(
          `[${this.connectionName}] Subscribers removed during backoff, cancelling reconnection`
        );
        this.onNoSubscribers();
      }
    }, delayInMs);
  }

  /**
   * Cancel any pending retry and reset retry state.
   */
  cancelRetry(): void {
    clearTimeout(this.retryTimerRef);
    this.retryTimerRef = undefined;
    this.isRetrying = false;
    this.retryCount = 0;
  }

  /**
   * Increment activity timeout count.
   * Returns true when the threshold is reached (should switch to polling).
   */
  incrementActivityTimeoutCount(): boolean {
    this.activityTimeoutCount++;
    return (
      this.activityTimeoutCount >= this.fallbackThreshold &&
      this.fallbackEnabled
    );
  }

  /**
   * Start (or restart) the SSE stability timer.
   * Only resets retryCount to 0 after SSE has been continuously active
   * for sseStabilityWindowMs.
   */
  startStabilityTracking(): void {
    // Already tracking — don't restart the timer on every message
    if (this.sseStabilityTimer) return;

    this.logger(
      `[${this.connectionName}] SSE stability tracking started (${this.sseStabilityWindowMs / 1000}s window)`
    );

    this.sseStabilityTimer = setTimeout(() => {
      this.sseStabilityTimer = undefined;
      const previousRetryCount = this.retryCount;
      const previousTimeoutCount = this.activityTimeoutCount;
      this.retryCount = 0;
      this.activityTimeoutCount = 0;
      this.logger(
        `[${this.connectionName}] SSE stable for ${this.sseStabilityWindowMs / 1000}s, resetting retryCount (was ${previousRetryCount}) and activityTimeoutCount (was ${previousTimeoutCount})`
      );
      diagnosticEventLog.record(this.diagnosticSource, "info", "retry-count-reset", {
        previousRetryCount,
        previousActivityTimeoutCount: previousTimeoutCount,
        stableForMs: this.sseStabilityWindowMs,
      });
    }, this.sseStabilityWindowMs);
  }

  /**
   * Clear the SSE stability timer (e.g. when connection drops).
   */
  clearStabilityTracking(): void {
    if (this.sseStabilityTimer) {
      clearTimeout(this.sseStabilityTimer);
      this.sseStabilityTimer = undefined;
    }
  }

  /**
   * Full cleanup — clears all timers and resets state.
   */
  destroy(): void {
    clearTimeout(this.retryTimerRef);
    this.retryTimerRef = undefined;
    this.clearStabilityTracking();
    this.isRetrying = false;
    this.retryCount = 0;
    this.activityTimeoutCount = 0;
  }
}
